import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";

// ── Lazy-loaded pages ─────────────────────────────────────────────────
const Landing          = lazy(() => import("./pages/Landing"));
const Login            = lazy(() => import("./pages/Login"));
const Signup           = lazy(() => import("./pages/Signup"));
const ForgotPassword   = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword    = lazy(() => import("./pages/ResetPassword"));
const GuestTest        = lazy(() => import("./pages/GuestTest"));
const PrivacyPolicy    = lazy(() => import("./pages/PrivacyPolicy"));
const About            = lazy(() => import("./pages/About"));
const Terms            = lazy(() => import("./pages/Terms"));
const TipsAndTricks    = lazy(() => import("./pages/TipsAndTricks"));
const Resources        = lazy(() => import("./pages/Resources"));

// Teacher-only pages
const Upload           = lazy(() => import("./pages/Upload"));
const ContentReview    = lazy(() => import("./pages/ContentReview"));
const TestBuilder      = lazy(() => import("./pages/TestBuilder"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const ClassroomManager = lazy(() => import("./pages/ClassroomManager"));

// Student-only pages
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const JoinClassroom    = lazy(() => import("./pages/JoinClassroom"));

// Shared (both roles)
const GeneratedTest    = lazy(() => import("./pages/GeneratedTest"));
const AttemptTest      = lazy(() => import("./pages/AttemptTest"));
const Library          = lazy(() => import("./pages/Library"));
const QuizMode         = lazy(() => import("./pages/QuizMode"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[var(--bg)]">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 rounded-full border-4 border-amber-400 border-t-transparent animate-spin" />
      <p className="text-sm text-[var(--text-muted)] font-medium">Loading…</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* ── Public ─────────────────────────────────────────────────── */}
      <Route path="/"                element={<Landing />} />
      <Route path="/login"           element={<Login />} />
      <Route path="/signup"          element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/take-test"       element={<GuestTest />} />
      <Route path="/privacy"         element={<PrivacyPolicy />} />
      <Route path="/about"           element={<About />} />
      <Route path="/terms"           element={<Terms />} />
      <Route path="/tips"            element={<TipsAndTricks />} />
      <Route path="/resources"       element={<Resources />} />

      {/* ── Teacher only ───────────────────────────────────────────── */}
      <Route element={<ProtectedRoute allowedRoles={["teacher"]} />}>
        <Route path="/dashboard/teacher"   element={<TeacherDashboard />} />
        <Route path="/classrooms"          element={<ClassroomManager />} />
      </Route>

      {/* ── Student only ───────────────────────────────────────────── */}
      <Route element={<ProtectedRoute allowedRoles={["student"]} />}>
        <Route path="/dashboard/student"   element={<StudentDashboard />} />
        <Route path="/my-classes"          element={<JoinClassroom />} />
      </Route>

      {/* ── Both roles ─────────────────────────────────────────────── */}
      <Route element={<ProtectedRoute />}>
        <Route path="/upload"              element={<Upload />} />
        <Route path="/review/:docId"       element={<ContentReview />} />
        <Route path="/builder/:docId"      element={<TestBuilder />} />
        <Route path="/library"             element={<Library />} />
        <Route path="/quiz/:docId"         element={<QuizMode />} />
        <Route path="/tests/:testId"       element={<GeneratedTest />} />
        <Route path="/attempt/:testId"     element={<AttemptTest />} />
      </Route>

      {/* ── Fallback ───────────────────────────────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "var(--surface)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: "0.75rem",
              },
            }}
          />
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}
